import { FC, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Shield, Loader2, ExternalLink, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import type { ConfigModalProps, IntegrationProvider } from '../types'

const OpenAttestIcon: FC = () => <img src="/vendor-logos/open-attest.svg" alt="Open-Attest" width={20} height={20} className="rounded-sm" />

function OpenAttestConfigModal({
  workspaceId,
  open,
  onOpenChange,
  onComplete,
  integration,
}: ConfigModalProps) {
  // If already connected, show manage view
  if (integration) {
    return (
      <OpenAttestManageModal
        workspaceId={workspaceId}
        open={open}
        onOpenChange={onOpenChange}
        integration={integration}
      />
    )
  }

  return (
    <OpenAttestConnectModal
      workspaceId={workspaceId}
      open={open}
      onOpenChange={onOpenChange}
      onComplete={onComplete}
    />
  )
}

function OpenAttestConnectModal({
  workspaceId,
  open,
  onOpenChange,
  onComplete,
}: {
  workspaceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: () => void
}) {
  const [serverUrl, setServerUrl] = useState('')
  const [apiKey, setApiKey] = useState('')

  const connectMutation = useMutation(
    trpc.integrations.openAttestConnect.mutationOptions()
  )

  const handleConnect = async () => {
    try {
      const result = await connectMutation.mutateAsync({ serverUrl, apiKey })
      toast.success(
        `Connected to Open-Attest (${result.devicesTotal} devices found)`
      )
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
      onComplete()
      onOpenChange(false)
    } catch (err: any) {
      toast.error(`Connection failed: ${err.message}`)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Connect Open-Attest</DialogTitle>
          <DialogDescription>
            Connect to your Open-Attest server to sync device posture data for compliance evidence.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="server-url">Server URL</Label>
            <Input
              id="server-url"
              placeholder="https://open-attest.your-org.workers.dev"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="api-key">API Key</Label>
            <Input
              id="api-key"
              type="password"
              placeholder="oak_..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-[11px] text-text-placeholder">
             Create an API key in the Open-Attest admin dashboard.
            </p>
          </div>

          <div className="rounded-sm border border-border bg-surface-subtle p-3">
            <p className="text-[12px] text-text-body">
              Need help setting up?{' '}
              <a
                href="https://github.com/Screenata/open-attest"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-cobalt-strong hover:underline"
              >
                Open-Attest documentation
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={!serverUrl || !apiKey || connectMutation.isPending}
          >
            {connectMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Shield className="h-4 w-4 mr-2" />
            )}
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function OpenAttestManageModal({
  workspaceId,
  open,
  onOpenChange,
  integration,
}: {
  workspaceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  integration: NonNullable<ConfigModalProps['integration']>
}) {
  const statusQuery = useQuery({
    ...trpc.integrations.openAttestGetStatus.queryOptions(),
    enabled: open,
  })

  const syncMutation = useMutation(
    trpc.integrations.openAttestSyncDevices.mutationOptions()
  )

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync()
      toast.success(
        `Synced ${result.synced} device(s)${result.removed ? `, removed ${result.removed}` : ''}`
      )
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.openAttestGetStatus.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.device.list.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.device.complianceStats.queryKey(),
      })
    } catch (err: any) {
      toast.error(`Sync failed: ${err.message}`)
    }
  }

  const status = statusQuery.data
  const metadata = integration.config as Record<string, any> | null
  const serverUrl = metadata?.serverUrl as string | undefined

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Open-Attest</DialogTitle>
          <DialogDescription>
            Manage your Open-Attest integration and sync device posture data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-4">
          {/* Connection status */}
          <div className="rounded-sm border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Status</span>
              {status && 'error' in status ? (
                <span className="flex items-center gap-1.5 text-[13px] text-status-orange">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Unreachable
                </span>
              ) : status?.connected ? (
                <span className="flex items-center gap-1.5 text-[13px] text-status-green">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Connected
                </span>
              ) : statusQuery.isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-text-placeholder" />
              ) : null}
            </div>

            {serverUrl && (
              <div>
                <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Server</span>
                <p className="text-[13px] text-text-body mt-0.5 break-all">{serverUrl}</p>
              </div>
            )}

            {status?.connected && status.stats && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Devices</span>
                  <p className="text-[16px] font-semibold text-text-secondary">{status.stats.devices_total}</p>
                </div>
                <div>
                  <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Compliant</span>
                  <p className="text-[16px] font-semibold text-status-green">{status.stats.devices_compliant}</p>
                </div>
                <div>
                  <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Non-Compliant</span>
                  <p className="text-[16px] font-semibold text-status-red">{status.stats.devices_non_compliant}</p>
                </div>
                <div>
                  <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Unknown</span>
                  <p className="text-[16px] font-semibold text-status-orange">{status.stats.devices_unknown}</p>
                </div>
              </div>
            )}

            {status?.connected && status.lastSyncAt && (
              <div>
                <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Last Sync</span>
                <p className="text-[13px] text-text-body mt-0.5">
                  {new Date(status.lastSyncAt).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleSync}
            disabled={syncMutation.isPending}
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 " />
            )}
            Sync Devices
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const OpenAttestProvider: IntegrationProvider = {
  id: 'OpenAttest',
  name: 'Open-Attest',
  description:
    'Endpoint attestation. Sync signed device posture (disk encryption, firewall, screen lock, AV/EDR) for SOC 2 evidence.',
  Icon: OpenAttestIcon,
  ConfigModal: OpenAttestConfigModal,
  handleConnect: async ({ onSuccess }) => {
    // Open the config modal — actual connection happens in the modal
    onSuccess?.({ provider: 'OpenAttest' })
    return { success: true }
  },
  getFeatures: () => [
    {
      id: 'device-posture',
      name: 'Device Posture Sync',
      description:
        'Sync endpoint security posture from enrolled devices (encryption, firewall, screen lock, AV/EDR).',
      enabled: true,
    },
    {
      id: 'signed-attestations',
      name: 'Signed Attestations',
      description:
        'Ed25519-signed attestation records for audit-ready evidence with cryptographic proof of origin.',
      enabled: true,
    },
  ],
  syncData: async (integration) => {
    await trpcClient.integrations.openAttestSyncDevices.mutate()
    toast.success('Device posture synced')
  },
}
